'use client';

import { YellowButton } from '@/components/button/button';
import { ChevronRight, ChevronRightRounded } from '@mui/icons-material';
import {
  Box,
  Container,
  FormControl,
  Input,
  InputProps,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import CurikoLogo from '../../assets/curiko-logo.png';
import Chevron from '../../assets/chevron.png';
import { RequestExperienceForm, submitAction } from '../../actions/submit';
import { useCurrentUser } from '@/hooks/use-current-user/use-current-user';
import Link from 'next/link';

const CustomTextField = (
  props: InputProps & {
    label: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  },
) => {
  const { label, value, onChange, ...rest } = props;
  return (
    <Stack spacing={1}>
      <Typography color="white" fontWeight="bold">
        {label}
      </Typography>
      <Input
        {...rest}
        value={value}
        onChange={onChange}
        disableUnderline
        sx={{
          backgroundColor: '#4C3B51',
          px: 2,
          py: 1,
          borderRadius: 2,
          color: 'white',
        }}
      />
    </Stack>
  );
};

export function RequestExperience() {
  const { data } = useCurrentUser();
  const accountEmail = data?.email;
  const accountName = data?.firstName;
  const id = data?.id;
  const [{ name, experience, inPerson, why, email }, setForm] = useState<RequestExperienceForm>({
    name: '',
    experience: '',
    inPerson: '',
    why: '',
    email: accountEmail || '',
    userId: id || '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (accountEmail) {
      setForm(prev => ({ ...prev, email: accountEmail }));
    }
  }, [accountEmail]);

  useEffect(() => {
    if (accountName) {
      setForm(prev => ({ ...prev, name: accountName }));
    }
  }, [accountName]);

  const handleChange = (newState: Partial<RequestExperienceForm>) => {
    setForm(prev => ({ ...prev, ...newState }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    handleChange({ [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    console.log('@@ handleSubmit', { name, experience, inPerson, why, email, id });
    e.preventDefault();
    setLoading(true);
    await submitAction({
      name,
      experience,
      inPerson,
      why,
      email: email || accountEmail || '',
      userId: id || '',
    });
    setLoading(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <Box py={4} sx={{ backgroundColor: '#251628', minHeight: '100vh' }}>
        <Container maxWidth="md">
          <Stack direction="column" spacing={4} my={10} alignItems="center" justifyContent="center">
            <Stack
              maxWidth={700}
              p={4}
              spacing={4}
              sx={{
                backgroundColor: theme => theme.palette.common.homepagePurple,
              }}
              borderRadius={4}
            >
              <Typography color="white" fontWeight="bold" textAlign="center">
                Your response has been submitted!
              </Typography>
              <Typography color="white" textAlign="center">
                Thanks for letting us know what you’re interested in. This helps us learn more about
                the kinds of experiences we should have and encourages us to reach out to hosts!
              </Typography>
            </Stack>
            <Stack direction="column" spacing={2} alignItems="center">
              <Link href="/" style={{ textDecoration: 'none' }}>
                <Typography
                  sx={{
                    color: theme => theme.palette.common.yellow,
                    textDecoration: 'underline',
                    textDecorationColor: theme => theme.palette.common.yellow,
                    textUnderlineOffset: 2,
                  }}
                >
                  Back to the homepage
                </Typography>
              </Link>
              <Typography
                color="white"
                sx={{
                  textDecoration: 'underline',
                  textDecorationColor: 'white',
                  textUnderlineOffset: 2,
                  cursor: 'pointer',
                }}
                onClick={() => window.history.back()}
              >
                Return to your previous page
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box py={4} sx={{ backgroundColor: '#251628', minHeight: '100vh' }}>
      <Container maxWidth="md">
        <Stack direction="row" spacing={4} my={10} alignItems="center">
          <Box flex={2}>
            <form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                <Typography variant="h1" color="white">
                  Curiosity Sparked?
                </Typography>
                <Typography color="white">
                  What experience(s) would you like to see on Curiko? These could be past
                  experiences or something new!
                </Typography>
                <Stack spacing={2}>
                  <CustomTextField
                    label="Your name *"
                    name="name"
                    value={name}
                    onChange={handleInputChange}
                    required
                  />
                  <CustomTextField
                    label="Email"
                    name="email"
                    value={email || ''}
                    onChange={handleInputChange}
                  />
                  <CustomTextField
                    label="What experience would you like to see *"
                    name="experience"
                    value={experience}
                    onChange={handleInputChange}
                    required
                  />
                  <FormControl fullWidth>
                    <Stack spacing={1}>
                      <Typography color="white" fontWeight="bold">
                        In person, or online?
                      </Typography>
                      <Select
                        name="inPerson"
                        value={inPerson}
                        onChange={e =>
                          handleChange({
                            inPerson: e.target.value as string,
                          })
                        }
                        variant="filled"
                        disableUnderline
                        hiddenLabel
                        MenuProps={{
                          BackdropProps: {
                            invisible: true,
                            sx: {
                              opacity: '0 !important',
                            },
                          },
                        }}
                        slotProps={{
                          input: {
                            sx: {
                              backgroundColor: '#4C3B51',
                              borderRadius: 2,
                            },
                          },
                        }}
                        sx={{
                          color: 'white',
                          '& .MuiSelect-icon': {
                            color: 'white', // Set your desired color here
                          },
                        }}
                      >
                        <MenuItem sx={{ p: 1 }} value="inPerson">
                          In person
                        </MenuItem>
                        <MenuItem sx={{ p: 1 }} value="online">
                          Online
                        </MenuItem>
                      </Select>
                    </Stack>
                  </FormControl>
                  <CustomTextField
                    label="Want to share why? (optional)"
                    name="why"
                    value={why}
                    onChange={handleInputChange}
                    multiline
                    rows={4}
                  />
                </Stack>
                <Box>
                  <YellowButton
                    type="submit"
                    endIcon={<Image src={Chevron} alt="" style={{ width: 8, height: 'auto' }} />}
                    disabled={!name || !experience || loading}
                  >
                    Submit
                  </YellowButton>
                </Box>
              </Stack>
            </form>
          </Box>
          <Box
            flex={1}
            sx={{
              display: {
                xs: 'none',
                md: 'block',
              },
            }}
          >
            <Image
              src={CurikoLogo}
              alt="Curiko Logo"
              style={{ maxWidth: 250, width: '100%', height: 'auto' }}
            />
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
